import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ventures } from "@/data/portfolio";
import { RateCard, BookingActions, BookingMeta } from "@/components/Booking";

/**
 * The ventures ledger. Four businesses, each with a live action.
 *
 * The card is deliberately not one big <Link>: a visitor either wants to read
 * about the place or wants to book it, and those are different destinations.
 * Wrapping everything in a route link would also nest anchors inside anchors.
 *
 * Composition alternates sides so four entries do not read as one molecule
 * stamped four times.
 */
export const VenturesSection = () => {
  return (
    <section
      id="ventures"
      className="relative bg-cream text-ink py-24 md:py-32"
      data-testid="ventures-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
          className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-14 md:mb-20"
        >
          <div className="col-span-12 md:col-span-7">
            <h2 className="font-display text-display-l text-ink">
              Four I run.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 md:col-start-9 md:text-right font-sans font-light text-base md:text-lg text-ink2 leading-relaxed">
            A retreat, a resort, a bar, and the cafe that started it. Three of
            them you can book today.
          </p>
        </motion.div>

        <div className="space-y-6 md:space-y-10">
          {ventures.map((v, i) => {
            const flip = i % 2 === 1;
            return (
              <motion.article
                key={v.slug}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-8% 0px" }}
                transition={{
                  duration: 1,
                  delay: (i % 2) * 0.06,
                  ease: [0.19, 1, 0.22, 1],
                }}
                className="group relative overflow-hidden bg-ink text-cream"
                data-testid={`venture-card-${v.slug}`}
              >
                <div className="grid grid-cols-12 items-stretch md:min-h-[520px]">
                  {/* Image */}
                  <Link
                    to={`/ventures/${v.slug}`}
                    aria-label={`${v.title}, read more`}
                    data-cursor="view"
                    className={`col-span-12 md:col-span-5 relative overflow-hidden aspect-[4/3] md:aspect-auto ${
                      flip ? "md:order-2" : ""
                    }`}
                  >
                    <img
                      src={v.hero}
                      alt={v.title}
                      loading={i < 1 ? "eager" : "lazy"}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.05]"
                    />
                    <span
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 45%, transparent 100%)",
                      }}
                    />
                    <span className="absolute bottom-5 left-5 label text-cream/80">
                      {v.location}
                    </span>
                  </Link>

                  {/* Body */}
                  <div
                    className={`col-span-12 md:col-span-7 px-7 md:px-14 py-11 md:py-14 flex flex-col ${
                      flip ? "md:order-1" : ""
                    }`}
                  >
                    <div className="flex-1">
                      <p className="label text-cream/55">
                        {v.role} · {v.year}
                      </p>

                      <h3 className="mt-5 font-display text-display-m">
                        <Link
                          to={`/ventures/${v.slug}`}
                          className="editorial-link"
                          data-cursor="hover"
                          data-testid={`venture-link-${v.slug}`}
                        >
                          {v.title}
                        </Link>
                      </h3>

                      <p className="mt-5 font-sans font-light text-lg md:text-xl text-cream/75 max-w-[46ch] leading-relaxed">
                        {v.tagline}
                      </p>

                      {v.rates.length > 0 ? (
                        <RateCard rates={v.rates} className="mt-9 max-w-[520px]" />
                      ) : (
                        <ul className="mt-9 grid grid-cols-2 gap-x-8 gap-y-3 max-w-[520px]">
                          {v.facts.slice(0, 4).map((f) => (
                            <li
                              key={f}
                              className="border-t border-cream/15 pt-3 label text-cream/60"
                            >
                              {f}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="mt-10">
                      {v.bookable ? (
                        <>
                          <BookingActions venture={v} tone="dark" />
                          <BookingMeta venture={v} tone="dark" className="mt-4" />
                        </>
                      ) : (
                        <Link
                          to={`/ventures/${v.slug}`}
                          data-cursor="hover"
                          data-testid={`venture-story-${v.slug}`}
                          className="group/s inline-flex items-center gap-3 label text-cream/70 hover:text-cream transition-colors"
                        >
                          Read the story
                          <ArrowRight
                            size={14}
                            className="transition-transform duration-300 group-hover/s:translate-x-1"
                          />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
